import {showMessage} from 'exsportia-components'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useQuery} from 'react-query'
import {useNavigate} from 'react-router-dom'

import {Class} from '../../../api/class/types'
import {ProviderStatusQueries} from '../../../api/provider-status'
import {readOpnChargeState} from '../../../api/provider-status/get-provider-status.query'
import {getOpnChargeState} from '../../../api/provider-status/helpers'
import {Service} from '../../../api/service/types'
import {SubscriptionBooking} from '../../../api/subscription-bookings/types'
import {Subscription} from '../../../api/subscriptions/types'
import {useVenue} from '../../../api/venue/hooks'
import {useLocale} from '../../../utils/i18n'
import {useAuthState, useHandleLoginState} from '../../auth/hooks'
import {UserService} from '../../user'
import {
  CheckoutGate,
  createSingleFlight,
  guardChargeSubmit,
  resolveCheckoutGate,
  resolveGateProvider,
} from '../helpers/checkout-gate'
import {PaymentService} from '../index'
import {PaymentTypes} from './payment-types'

export {PaymentTypes}

// const paymentTypes = ['payOnLocation', 'payNow', 'oneTimePayment']

export type PaymentMethodToken = string

export type NullablePaymentMethodToken = PaymentMethodToken | null

export type PaymentSubmitParams = {
  paymentMethodToken: NullablePaymentMethodToken
  paymentType: PaymentTypes
}

export type PaymentSubmit = (params: PaymentSubmitParams) => void

type Return = {
  isLoading: boolean
  paymentType: PaymentTypes
  allowedOptions: PaymentTypes[]
  setPaymentType: (_paymentType: PaymentTypes) => void
  submitWithPayment: () => void
  paymentMethodToken: NullablePaymentMethodToken
  setUserPaymentToken: (_token: PaymentMethodToken) => void
  isPayOnLocationAllowed: boolean
  isPayOnExsportiaAllowed: boolean
  isOneTimePaymentAllowed: boolean
  initialPaymentType: PaymentTypes
  checkoutGate: CheckoutGate
  /** The submit-time status read is in flight: keep the pay button busy so it can't be pressed twice. */
  isCheckingCharge: boolean
}

type GateArgs = {
  /** The amount to charge, when the page knows it: 0 (free / 100 % promo) is never gated. */
  totalPrice?: number | null
  /** false for a flow that only saves a card and charges nothing. */
  chargesOnSubmit?: boolean
}

type ClassArgs = GateArgs & {
  entityType: 'class'
  entity?: Class
  submit: PaymentSubmit
  redirectLoggedOut?: boolean
  filterAllowedOptions?: PaymentTypes[]
}

type ServiceArgs = GateArgs & {
  entityType: 'service'
  entity?: Service
  submit: PaymentSubmit
  redirectLoggedOut?: boolean
  filterAllowedOptions?: PaymentTypes[]
}

type SubscriptionArgs = GateArgs & {
  entityType: 'subscription'
  entity?: Subscription
  submit: PaymentSubmit
  redirectLoggedOut?: boolean
  filterAllowedOptions?: PaymentTypes[]
}

type SubscriptionBookingArgs = GateArgs & {
  entityType: 'subscription-booking'
  entity?: SubscriptionBooking
  submit: PaymentSubmit
  redirectLoggedOut?: boolean
  filterAllowedOptions?: PaymentTypes[]
}

type OptionsFoo = (_args: ClassArgs | ServiceArgs | SubscriptionArgs | SubscriptionBookingArgs) => Return

const defaultFilteredOptions = [] satisfies PaymentTypes[]

export const usePaymentOptions: OptionsFoo = ({
  entityType,
  entity,
  submit,
  redirectLoggedOut = true,
  filterAllowedOptions = defaultFilteredOptions,
  totalPrice,
  chargesOnSubmit = true,
}) => {
  const navigate = useNavigate()
  const {l} = useLocale()
  const {data: venue, isFetching: isVenueFetching} = useVenue()
  const {isLoggedIn} = useAuthState()
  const {data: terminal, isFetching: isTerminalFetching} = useQuery(
    PaymentService.queries.getTerminalQuery({
      params: {
        venueId: venue?.uid,
      },
      refetchOnWindowFocus: false,
    }),
  )
  const {
    data: userPaymentMethods,
    remove,
    refetch,
    isFetching: isUserPaymentMethodsFetching,
  } = useQuery(
    UserService.queries.getUserPaymentMethodsQuery({
      params: {venueId: venue?.uid},
      enabled: isLoggedIn,
      refetchOnWindowFocus: false,
    }),
  )
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refetch])
  const userCanPayNow = useMemo(
    () => userPaymentMethods?.paymentMethods && userPaymentMethods.paymentMethods.length !== 0,
    [userPaymentMethods],
  )

  const isPayOnLocationAllowed = useMemo(() => {
    if (!entity || filterAllowedOptions.includes(PaymentTypes.PAY_ON_LOCATION)) {
      return false
    }
    if (entityType !== 'subscription' && entityType !== 'subscription-booking') {
      return entity.paymentOnArrival
    }
    return false
  }, [entity, filterAllowedOptions])

  const handleLoggedOut = useCallback(() => {
    if (!isPayOnLocationAllowed && redirectLoggedOut) {
      navigate(-1)
      return
    }
  }, [isPayOnLocationAllowed])

  useHandleLoginState({
    handleLoggedOut,
    handleUserUpdated: remove,
  })
  const isPayOnExsportiaAllowed = useMemo(() => {
    if (!entity || filterAllowedOptions.includes(PaymentTypes.PAY_NOW)) {
      return false
    }
    if (entityType !== 'subscription' && entityType !== 'subscription-booking') {
      return Boolean(entity.paymentOnExsportia && terminal)
    }

    return Boolean(terminal)
  }, [entity, terminal, filterAllowedOptions, userPaymentMethods])

  const isOneTimePaymentAllowed = useMemo(() => {
    if (!entity || filterAllowedOptions.includes(PaymentTypes.ONE_TIME)) {
      return false
    }
    return Boolean(isPayOnExsportiaAllowed && terminal?.oneTimePayment)
  }, [entity, terminal, isPayOnExsportiaAllowed])

  const initialPaymentType = useMemo(() => {
    if (isPayOnExsportiaAllowed && userCanPayNow) {
      return PaymentTypes.PAY_NOW
    }
    if (isOneTimePaymentAllowed) {
      return PaymentTypes.ONE_TIME
    }
    return PaymentTypes.PAY_ON_LOCATION
  }, [isPayOnExsportiaAllowed, isPayOnLocationAllowed, isOneTimePaymentAllowed])

  const allowedOptions = useMemo(() => {
    const allowed = []
    if (isPayOnExsportiaAllowed) {
      allowed.push(PaymentTypes.PAY_NOW)
    }
    if (isOneTimePaymentAllowed) {
      allowed.push(PaymentTypes.ONE_TIME)
    }
    if (isPayOnLocationAllowed) {
      allowed.push(PaymentTypes.PAY_ON_LOCATION)
    }
    return allowed
  }, [isPayOnExsportiaAllowed, isPayOnLocationAllowed, isOneTimePaymentAllowed])

  const [paymentMethodToken, setPaymentMethodToken] = useState<string | null>(null)
  const [paymentType, setPaymentType] = useState(initialPaymentType)

  useEffect(() => {
    const impossibleToSetPaymentMethodToken =
      paymentType === PaymentTypes.ONE_TIME || paymentType === PaymentTypes.PAY_ON_LOCATION
    if (impossibleToSetPaymentMethodToken) {
      setPaymentMethodToken(null)
    }
  }, [paymentType])

  useEffect(() => {
    if (userCanPayNow) {
      setPaymentType(PaymentTypes.PAY_NOW)
      // eslint-disable-next-line
      setPaymentMethodToken(userPaymentMethods!.paymentMethods![0].uid)
    }
  }, [userCanPayNow])

  // Read when the pay step opens (never blocks rendering) and again on submit, from the 60 s cache.
  const {data: providerStatus} = useQuery(
    ProviderStatusQueries.getProviderStatusQuery({
      params: {venueId: venue?.uid},
      enabled: Boolean(venue?.uid) && chargesOnSubmit,
    }),
  )
  const gatedProvider = chargesOnSubmit ? resolveGateProvider(terminal) : null
  const checkoutGate = useMemo(
    () => resolveCheckoutGate({
      provider: gatedProvider,
      chargeState: getOpnChargeState(providerStatus),
      totalPrice,
      allowedOptions,
      paymentType,
    }),
    [gatedProvider, providerStatus, totalPrice, allowedOptions, paymentType],
  )

  // Busy from before the status read until submit() has been handed the payment, so a second
  // click while the read is in flight (up to 3 s on a cold cache) can't queue a second charge.
  const singleFlight = useRef(createSingleFlight()).current
  const [isCheckingCharge, setIsCheckingCharge] = useState(false)
  const submitWithPayment = useCallback(() => {
    void singleFlight(async () => {
      setIsCheckingCharge(true)
      try {
        await guardChargeSubmit({
          provider: gatedProvider,
          totalPrice,
          paymentType,
          readChargeState: () => readOpnChargeState(venue?.uid),
          submit: () => submit({paymentType, paymentMethodToken}),
          onBlocked: () => showMessage(l('checkout-gate-unavailable'), 'error'),
        })
      } finally {
        setIsCheckingCharge(false)
      }
    })
  }, [singleFlight, paymentType, paymentMethodToken, submit, gatedProvider, totalPrice, venue?.uid, l])

  const setUserPaymentToken = useCallback((paymentToken: string) => {
    setPaymentType(PaymentTypes.PAY_NOW)
    setPaymentMethodToken(paymentToken)
  }, [])

  return {
    isLoading: isUserPaymentMethodsFetching || isVenueFetching || isTerminalFetching,
    allowedOptions,
    submitWithPayment,
    paymentType,
    paymentMethodToken,
    setUserPaymentToken,
    setPaymentType,
    isPayOnLocationAllowed,
    isPayOnExsportiaAllowed,
    isOneTimePaymentAllowed,
    initialPaymentType,
    checkoutGate,
    isCheckingCharge,
  }
}
